import {useCallback, useState} from 'react';

import type {User} from 'nutshell-core/users/users-types';

import {Stack} from 'shells/layout/stack/stack';

import type {AccountPageFragment, ContactPageFragment} from 'nutshell-graphql-types';

import type {PeepType} from '../../../types';

import {appHistory} from '../../../../../history';
import {useDismissibleUserSetting} from '../../../../../util/hooks/use-dismissible-user-setting';
import {CustomizeLayoutButton} from '../../../../core-entity/layout-customizer/customize-layout-button';
import {
    getLayoutCustomizerPath,
    getLayoutCustomizerViewedSetting,
} from '../../../../core-entity/layout-customizer/helpers';
import {PeepAssigneeSelect} from '../../../../entity-details/peep-details/peep-assignee-select';
import {PeepSendTo} from '../../../../entity-details/peep-send-to';

import {CreateInfo} from './create-info/create-info';
import {PeepHeaderActionMenu} from './peep-header-action-menu';
import {PeepNameContainer} from './peep-name-container';

import styles from '../../../peep-page.module.css';

type PeepHeaderData = Pick<
    AccountPageFragment,
    'id' | 'name' | 'createdTime' | 'creatorUser' | 'owner' | 'isDeleted'
> &
    Pick<
        ContactPageFragment,
        'id' | 'name' | 'createdTime' | 'creatorUser' | 'owner' | 'isDeleted'
    >;

interface Props {
    peep: PeepHeaderData;
    user: User;
    entityType: PeepType;
    assigneeId: string | null;
    assigneePlaceholder: string;
    onUpdateName: (name: string) => Promise<any>;
    onUpdateAssignee: (assigneeId: string | null) => Promise<any>;
    onUpdateDeletedStatus: (isDeleted: boolean) => Promise<any>;
    onHardDelete: () => Promise<any>;
    isUnsubscribedFromAll?: boolean;
    nameAdornment?: React.ReactNode;
}

export function PeepHeaderActionBar(props: Props) {
    const [isUpdatingAssignee, setIsUpdatingAssignee] = useState<boolean>(false);

    const layoutEntityType = props.entityType === 'account' ? 'ACCOUNT' : 'CONTACT';
    const {onDismiss} = useDismissibleUserSetting(
        getLayoutCustomizerViewedSetting(layoutEntityType)
    );

    const handleCustomizeLayout = useCallback(() => {
        appHistory.push(getLayoutCustomizerPath(layoutEntityType));
        if (onDismiss) {
            onDismiss();
        }
    }, [layoutEntityType, onDismiss]);

    const handleUpdateAssignee = (assigneeId?: string | null | undefined) => {
        if (isUpdatingAssignee) {
            return;
        }

        setIsUpdatingAssignee(true);
        props.onUpdateAssignee(assigneeId ?? null).finally(() => {
            setIsUpdatingAssignee(false);
        });
    };

    const showCustomizeLayoutInMenu =
        NutClientConfig.hasCoreEntityFullHeightSidebarFF && props.user.permissions.canAccessSetup;

    return (
        <Stack isHorizontal={true} spacing={16} justifyContent='space-between' alignItems='center'>
            <Stack isHorizontal={true} spacing={4} alignItems='center'>
                <PeepNameContainer
                    value={props.peep.name}
                    onSaveNewValue={props.onUpdateName}
                    entityType={props.entityType}
                    isDeleted={props.peep.isDeleted ?? false}
                />
                {props.nameAdornment}
            </Stack>
            <Stack isHorizontal={true} spacing={8} alignItems='center'>
                {!NutClientConfig.hasCoreEntityFullHeightSidebarFF && (
                    <CreateInfo
                        creatorUser={props.peep.creatorUser}
                        createdTime={props.peep.createdTime}
                    />
                )}
                <div className={styles['assignee-select-container']}>
                    <PeepAssigneeSelect
                        assigneeId={props.assigneeId}
                        onUpdatePeepAssignee={handleUpdateAssignee}
                        withControlIcon={false}
                        placeholder={props.assigneePlaceholder}
                        isDisabled={!props.user.permissions.canAssignEntities || isUpdatingAssignee}
                    />
                </div>
                <PeepSendTo
                    peepId={props.peep.id}
                    iconOnly={NutClientConfig.hasCoreEntityFullHeightSidebarFF}
                />
                {!NutClientConfig.hasCoreEntityFullHeightSidebarFF &&
                    props.user.permissions.canAccessSetup && (
                        <CustomizeLayoutButton text='Layout' entityType={layoutEntityType} />
                    )}
                <PeepHeaderActionMenu
                    entityType={props.entityType}
                    entityId={props.peep.id}
                    entityName={props.peep.name}
                    isSoftDeleted={props.peep.isDeleted ?? false}
                    onDelete={() => props.onUpdateDeletedStatus(true)}
                    onUndelete={() => props.onUpdateDeletedStatus(false)}
                    onPermanentlyDelete={props.onHardDelete}
                    isUnsubscribedFromAll={props.isUnsubscribedFromAll}
                    onCustomizeLayout={
                        showCustomizeLayoutInMenu ? handleCustomizeLayout : undefined
                    }
                />
            </Stack>
        </Stack>
    );
}
